import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    searchAndReplace: {
      /**
       * Find and highligh matches in the content
       * @param searchText The search text
       * @example editor.commands.search('find me')
       */
      search: (searchText: string) => ReturnType
      /**
       * Replace the selection with the replace term
       * @example editor.commands.replace('replace ', true)
       */
      replace: (replaceTerm: string, replaceAll: boolean) => ReturnType
      /**
       * Clear search highlights
       * @example editor.commands.clearSearch()
       */
      clearSearch: () => ReturnType
      /**
       * Focus on previous match
       * @example editor.commands.prevMatch()
       */
      prevMatch: () => ReturnType
      /**
       * Focus on next match
       * @example editor.commands.nextMatch()
       */
      nextMatch: () => ReturnType
      /**
       * set the case option
       * @example editor.commands.setCase()
       */
      setCase: (matchCase: boolean) => ReturnType
      /**
       * set the flag for matching partial words
       * @example editor.commands.setWholeWord(false)
       */
      setWholeWord: (wholeWord: boolean) => ReturnType
    }
  }
}

const pluginKey = new PluginKey('searchAndReplace')

export const SearchAndReplace = Extension.create({
  name: 'searchAndReplace',

  addOptions() {
    return {
      highlightClass: 'search-highlight', // CSS class for highlighting
      highlightClassActive: 'search-highlight-active' // CSS class for highlighting active match
    }
  },

  // In v3, `storage` should be used for mutable runtime state.
  addStorage() {
    return {
      matches: [] as Array<{ from: number; to: number }>, // Store search result positions
      activeMatchIndex: 0 as number | null, // Keep track of the active match for navigation
      matchCase: false,
      matchWholeWord: false
    }
  },

  addCommands() {
    return {
      // Search for matches in the document
      search:
        (searchTerm) =>
        ({ editor }) => {
          if (!searchTerm) {
            editor.commands.clearSearch()
            return false
          }

          const createRegex = (searchTerm: string, matchCase = false, matchWholeWord = false) => {
            // Escape any special characters in the searchTerm
            const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

            // Add word boundary anchors if matchWholeWord is true
            const pattern = matchWholeWord ? `\\b${escapedSearchTerm}\\b` : escapedSearchTerm

            // Add 'i' flag for case-insensitive search if matchCase is false
            const flags = matchCase ? 'g' : 'gi'

            // Create and return the regular expression
            return new RegExp(pattern, flags)
          }

          const { doc } = editor.state
          const { matchCase, matchWholeWord } = this.storage
          const regex = createRegex(searchTerm, matchCase, matchWholeWord)
          const matches: { from: number; to: number }[] = []

          // Clear previous matches
          this.storage.matches = []

          // Iterate through the document to find matches
          doc.descendants((node, pos) => {
            if (node.isText && node.text) {
              let match
              while ((match = regex.exec(node.text)) !== null) {
                // Calculate positions and push matches
                const from = pos + match.index
                const to = from + match[0].length

                if (from >= 0 && to <= doc.content.size) {
                  matches.push({ from, to })
                } else {
                  console.warn('Invalid match positions:', { from, to })
                }
              }
            }
          })

          this.storage.matches = matches

          // Reset the active match index
          this.storage.activeMatchIndex = matches.length ? 0 : null

          // In TipTap v3, we need to trigger decoration refresh via a no-op transaction
          editor.view.dispatch(editor.state.tr)

          return true
        },

      // Replace matched text with a given term
      replace:
        (replaceTerm, replaceAll = false) =>
        ({ editor, tr, dispatch }) => {
          if (!this.storage.matches.length) {
            return false // No matches to replace
          }

          let offset = 0 // This will keep track of the total shift in positions
          const { matches, activeMatchIndex } = this.storage

          if (replaceAll) {
            // Replace all matches and account for shifting positions
            matches.forEach(({ from, to }: { from: number; to: number }) => {
              const adjustedFrom = from + offset
              const adjustedTo = to + offset
              tr = tr.insertText(replaceTerm, adjustedFrom, adjustedTo) // Update the transaction reference

              // Update offset based on the difference between searchTerm and replaceTerm
              const lengthDifference = replaceTerm.length - (to - from)
              offset += lengthDifference // Adjust the offset for future matches
            })

            if (dispatch) dispatch(tr) // Dispatch transaction safely
            this.storage.matches = [] // Clear matches after replacing all
            this.storage.activeMatchIndex = null // Reset active match index
          } else {
            // Replace only the active match and update the match list accordingly
            const index = activeMatchIndex ?? 0
            const { from, to } = matches[index]
            const adjustedFrom = from + offset
            const adjustedTo = to + offset
            tr = tr.insertText(replaceTerm, adjustedFrom, adjustedTo)

            // Remove the replaced match and adjust active index
            this.storage.matches.splice(index, 1)
            if (this.storage.activeMatchIndex !== null) {
              if (this.storage.activeMatchIndex >= this.storage.matches.length) {
                this.storage.activeMatchIndex = this.storage.matches.length - 1
              }
            }
            if (dispatch) dispatch(tr) // Dispatch transaction safely
          }

          // Force re-decoration
          editor.view.dispatch(editor.state.tr)

          return true
        },

      // Clear search highlights
      clearSearch:
        () =>
        ({ editor }) => {
          this.storage.matches = []
          this.storage.activeMatchIndex = 0
          editor.view.dispatch(editor.state.tr)
          return true
        },

      // Navigate to the next match
      nextMatch:
        () =>
        ({ editor }) => {
          if (!this.storage.matches.length) return false
          const currentIndex = this.storage.activeMatchIndex ?? 0
          this.storage.activeMatchIndex =
            (currentIndex + 1) % this.storage.matches.length

          editor.view.dispatch(editor.state.tr)

          const currentMatch = this.storage.matches[this.storage.activeMatchIndex]
          editor.commands.setTextSelection({ from: currentMatch.from, to: currentMatch.to })
          editor.commands.scrollIntoView()
          return true
        },

      // Navigate to the previous match
      prevMatch:
        () =>
        ({ editor }) => {
          if (!this.storage.matches.length) return false
          const currentIndex = this.storage.activeMatchIndex ?? 0
          this.storage.activeMatchIndex =
            (currentIndex - 1 + this.storage.matches.length) %
            this.storage.matches.length

          editor.view.dispatch(editor.state.tr)

          const currentMatch = this.storage.matches[this.storage.activeMatchIndex]
          editor.commands.setTextSelection({ from: currentMatch.from, to: currentMatch.to })
          editor.commands.scrollIntoView()
          return true
        },

      // Toggle match case option
      setCase:
        (matchCase) =>
        () => {
          this.storage.matchCase = matchCase
          return false
        },

      // Toggle whole word option
      setWholeWord:
        (wholeWord) =>
        () => {
          this.storage.matchWholeWord = wholeWord
          return false
        }
    }
  },

  addProseMirrorPlugins() {
    const extensionInstance = this // Capture `this` context

    return [
      new Plugin({
        key: pluginKey,

        props: {
          decorations: (state) => {
            const { doc } = state
            const decorations = []
            const matches = extensionInstance.storage.matches || []

            // Highlight all matches
            matches.forEach(({ from, to }: { from: number; to: number }) => {
              decorations.push(
                Decoration.inline(from, to, {
                  class: extensionInstance.options.highlightClass,
                })
              )
            })


            // Highlight the active match differently
            const activeIndex = extensionInstance.storage.activeMatchIndex
            const activeMatch =
              activeIndex != null ? matches[activeIndex] : null

            if (activeMatch) {
              decorations.push(
                Decoration.inline(activeMatch.from, activeMatch.to, {
                  class: extensionInstance.options.highlightClassActive
                })
              )
            }

            return DecorationSet.create(doc, decorations)
          }
        }
      })
    ]
  }
})
